import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, ArrowRight, RotateCcw, UserPlus, BrainCircuit, GitBranch, Database, MessageCircle, Clock, Check } from "lucide-react";
import { scrollToId } from "@/lib/site";
import { Reveal, SectionLabel, GlowButton } from "@/components/primitives";

export default function AutomationDemo() {
  const [step, setStep] = useState(-1);
  const [running, setRunning] = useState(false);
  const timers = useRef([]);

  const clear = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  const run = useCallback(() => {
    clear();
    setRunning(true);
    setStep(-1);
    for (let i = 0; i <= 5; i++) {
      const t = setTimeout(() => {
        setStep(i);
        if (i === 5) setRunning(false);
      }, 300 + i * 800);
      timers.current.push(t);
    }
  }, []);

  const reset = () => {
    clear();
    setRunning(false);
    setStep(-1);
  };

  const on = (n) => step >= n;

  return (
    <section className="relative py-20 sm:py-28 lg:py-32" data-testid="automation-demo-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:gap-16">
          <Reveal>
            <SectionLabel color="#65A30D">Live Demo</SectionLabel>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#111827] sm:text-5xl">
              Turn repetitive work into <span className="text-gradient">automatic</span> work.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Press play and watch a single lead flow through qualification, CRM and follow-up —
              with no manual steps in between.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <GlowButton onClick={run} disabled={running} data-testid="automation-sim-button">
                <Play className="h-4 w-4" />
                {running ? "Running…" : "Run the flow"}
              </GlowButton>
              <GlowButton variant="secondary" onClick={reset} data-testid="automation-reset-button">
                <RotateCcw className="h-4 w-4" /> Reset
              </GlowButton>
            </div>
            <GlowButton variant="dark" onClick={() => scrollToId("contact")} className="mt-6" data-testid="automation-cta-workflow">
              Automate Your Workflow
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </GlowButton>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative rounded-[2rem] bg-[#F8FAFC] p-6 ring-1 ring-slate-100 sm:p-10">
              <div className="mx-auto flex max-w-sm flex-col items-center">
                <FlowNode active={on(0)} color="#2563EB" icon={UserPlus} label="New Lead" sub="Form / Ad click" testid="automation-node-lead" />
                <Connector active={on(1)} color="#2563EB" />
                <FlowNode active={on(1)} color="#7C3AED" icon={BrainCircuit} label="AI Check" sub="Intent scored" testid="automation-node-ai" />
                <Connector active={on(2)} color="#7C3AED" />
                <FlowNode active={on(2)} color="#FF6B35" icon={GitBranch} label="Qualified?" sub="AI decision" testid="automation-node-decision" />

                {/* branch */}
                <div className="relative mt-2 grid w-full grid-cols-2 gap-4">
                  <div className="absolute left-1/2 top-0 h-5 w-px -translate-x-1/2 bg-slate-200">
                    <motion.span className="absolute inset-0 w-px bg-[#FF6B35]" initial={{ height: 0 }} animate={{ height: on(3) ? "100%" : 0 }} transition={{ duration: 0.4 }} />
                  </div>

                  <div className="flex flex-col items-center pt-5">
                    <Pill active={on(3)} color="#65A30D" label="YES" />
                    <Connector active={on(4)} color="#65A30D" small />
                    <FlowNode active={on(4)} color="#2563EB" icon={Database} label="CRM" sub="Record synced" compact testid="automation-node-crm" />
                    <Connector active={on(5)} color="#2563EB" small />
                    <FlowNode active={on(5)} color="#10B981" icon={MessageCircle} label="WhatsApp" sub="Nudge sent" compact testid="automation-node-whatsapp" />
                  </div>

                  <div className="flex flex-col items-center pt-5">
                    <Pill active={on(3)} color="#94A3B8" label="NO" />
                    <Connector active={on(4)} color="#94A3B8" small />
                    <FlowNode active={on(4)} color="#FF6B35" icon={Clock} label="Follow-up" sub="Re-engage later" compact testid="automation-node-followup" />
                  </div>
                </div>

                {step >= 5 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#111827] px-4 py-2 text-sm font-semibold text-white"
                  >
                    <Check className="h-4 w-4 text-[#B7F34A]" /> Meeting booked automatically
                  </motion.div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FlowNode({ active, color, icon: Icon, label, sub, compact, testid }) {
  return (
    <motion.div
      animate={{ scale: active ? 1 : 0.98, opacity: active ? 1 : 0.55 }}
      transition={{ duration: 0.3 }}
      className={`flex w-full items-center gap-3 rounded-2xl bg-white ${compact ? "px-3 py-2.5" : "px-4 py-3.5"} shadow-card ring-1`}
      style={{ borderColor: active ? color : "transparent", boxShadow: active ? `0 10px 30px -10px ${color}66` : undefined, ...(active ? { "--tw-ring-color": color } : {}) }}
      data-testid={testid}
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-white" style={{ background: color }}>
        <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
      </span>
      <div className="min-w-0">
        <div className="font-display text-sm font-bold text-[#111827]">{label}</div>
        <div className="truncate text-[11px] text-slate-500">{sub}</div>
      </div>
    </motion.div>
  );
}

function Connector({ active, color, small }) {
  return (
    <div className={`relative w-px ${small ? "h-4" : "h-6"} bg-slate-200`}>
      <motion.span className="absolute inset-0 w-px" style={{ background: color }} initial={{ height: 0 }} animate={{ height: active ? "100%" : 0 }} transition={{ duration: 0.35 }} />
    </div>
  );
}

function Pill({ active, color, label }) {
  return (
    <motion.span
      animate={{ opacity: active ? 1 : 0.4, scale: active ? 1 : 0.9 }}
      className="rounded-full px-3 py-1 text-[11px] font-bold text-white"
      style={{ background: color }}
    >
      {label}
    </motion.span>
  );
}
